import { Box, Button, FormControl, FormHelperText, Grid, Paper, TextField, Typography } from "@mui/material";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

// Types
import { RootState } from "../../../app/store";
import { DocumentoEmail, ISdocumentoEmail } from "../../../types/documento/email/DocumentoEmail";
import { DocumentoEmailDTO } from "../../../types/documento/email/DocumentoEmailDTO";

// Config
import configUrls from "../../../config/configUrls";

// Utils
import errorFormat from "../../../utils/errorFormat";

// API
import { useCreateEmailDocumentoMutation, useGetDocumentoEmailQuery } from "./DocumentoEmailApiSlice";

// ReactQuill Configuration
const QUILL_MODULES = {
    toolbar: [
        [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
        [{ 'size': ['small', false, 'large', 'huge'] }],
        ['bold', 'italic', 'underline', 'strike', 'blockquote'],
        [{ 'list': 'ordered' }, { 'list': 'bullet' }, { 'indent': '-1' }, { 'indent': '+1' }],
        [{ 'script': 'sub' }, { 'script': 'super' }],
        [{ 'direction': 'rtl' }],
        [{ 'color': [] }, { 'background': [] }],
        [{ 'align': [] }],
        ['link', 'image', 'video'],
        ['clean'],
        [{ 'code-block': true }],
        [{ 'table': true }]
    ],
};

const QUILL_FORMATS = [
    'header',
    'size',
    'bold', 'italic', 'underline', 'strike', 'blockquote',
    'list', 'bullet', 'indent',
    'script',
    'direction',
    'color', 'background',
    'align',
    'link', 'image', 'video',
    'code-block',
    'table'
];

export const DocumentoEmailCreateForm = () => {
    // Hooks
    const navigate = useNavigate();
    const location = useLocation();
    const { enqueueSnackbar } = useSnackbar();
    const { processoSelecionado } = useSelector((state: RootState) => state.processoSelecionado);

    // URL Parameters
    const idDocumento = useParams().id || "";
    const params = new URLSearchParams(location.search);
    const idEmail = params.get('idEmail');

    // State
    const [documentoEmail, setDocumentoEmail] = useState<DocumentoEmail>(ISdocumentoEmail);
    const [emailError, setEmailError] = useState<string>("");

    // API Hooks
    const [createEmail, status] = useCreateEmailDocumentoMutation();
    const { data: dataDoc, isFetching: isFetchingDoc, error: errorDoc } = useGetDocumentoEmailQuery(idEmail || "");

    // Email Validation
    const validateEmail = (email: string): boolean => {
        const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
        return emailRegex.test(email);
    };

    // Handlers
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        const cursorPosition = e.target.selectionStart;

        if (name === "para" && value !== "") {
            setEmailError(validateEmail(value) ? "" : "Email inválido");
        }

        setDocumentoEmail(prev => ({
            ...prev,
            [name]: value
        }));

        setTimeout(() => {
            const input = e.target;
            input.setSelectionRange(cursorPosition, cursorPosition);
        }, 0);
    };

    const handleQuillChange = (content: string) => {
        setDocumentoEmail(prev => ({
            ...prev,
            mensagem: content
        }));
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (emailError) {
            enqueueSnackbar("Por favor, corrija o email antes de enviar", { variant: "error" });
            return;
        }

        const emailData: DocumentoEmailDTO = {
            id: documentoEmail.id,
            para: documentoEmail.para,
            assunto: documentoEmail.assunto,
            mensagem: documentoEmail.mensagem,
            documentoID: idDocumento
        };

        await createEmail(emailData);
    };

    // Effects
    useEffect(() => {
        if (!processoSelecionado) {
            enqueueSnackbar("Nenhum documento foi previamente selecionado", { variant: "error" });
            navigate(configUrls.PROCESSO_LIST_RT);
        }
    }, [processoSelecionado, navigate, enqueueSnackbar]);

    useEffect(() => {
        if (dataDoc) {
            setDocumentoEmail(dataDoc);
        } else if (errorDoc) {
            enqueueSnackbar("Documento não encontrado!", { variant: "error" });
        } else if (isFetchingDoc) {
            enqueueSnackbar("Buscando registro...", { variant: "info" });
        } else {
            enqueueSnackbar("Documento não encontrado!", { variant: "error" });
            navigate(configUrls.PROCESSO_LIST_RT);
        }
    }, [dataDoc, errorDoc, isFetchingDoc, navigate, enqueueSnackbar]);

    useEffect(() => {
        if (status.isError) {
            enqueueSnackbar("Registro não incluído!", { variant: "error" });
            const erroReturn: string = errorFormat(status);
            enqueueSnackbar(erroReturn, { variant: "error" });
        }

        if (status.isSuccess) {
            enqueueSnackbar("Registro incluído!", { variant: "success" });
            navigate(-1);
        }
    }, [status, navigate, enqueueSnackbar]);

    return (
        <Paper>
            <Typography variant="h5" component="h1" sx={{ padding: 2 }}>
                Enviar Email.
            </Typography>
          

            <Box mt={2} p={2}>
                <form onSubmit={handleSubmit} method='POST'>
                    <Grid container spacing={1}>
                        <Grid item xs={12}>
                            <FormControl fullWidth error={!!emailError}>
                                <TextField
                                    required
                                    label="Destinatário:"
                                    name="para"
                                    onChange={handleChange}
                                    size="small"
                                    inputProps={{ maxLength: 100 }}
                                    value={documentoEmail.para}
                                    error={!!emailError}
                                    helperText={documentoEmail.para ? (emailError ? emailError : `${documentoEmail.para.length}/100 caracteres`) : ""}
                                />
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <TextField
                                    required
                                    label="Assunto:"
                                    name="assunto"
                                    onChange={handleChange}
                                    size="small"
                                    inputProps={{ maxLength: 255 }}
                                    value={documentoEmail.assunto}
                                    helperText={documentoEmail.assunto ? `${documentoEmail.assunto.length}/255 caracteres` : ""}
                                />
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Mensagem:</Typography>
                                <ReactQuill
                                    theme="snow"
                                    value={documentoEmail.mensagem}
                                    onChange={handleQuillChange}
                                    modules={QUILL_MODULES}
                                    formats={QUILL_FORMATS}
                                    style={{ height: '200px', marginBottom: '50px' }}
                                />
                                <FormHelperText>
                                    {documentoEmail.mensagem ? `${documentoEmail.mensagem.replace(/<[^>]*>/g, '').length}/255 caracteres` : ""}
                                </FormHelperText>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <Box display="flex" gap={2}>
                            {idDocumento !== "" && idDocumento !== null &&(
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="secondary"
                                >
                                    Enviar
                                </Button>
                            )}

                                <Button
                                    variant="contained"
                                    onClick={() => navigate(-1)}
                                >
                                    Voltar
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </form>
            </Box>
        </Paper>
    );
};